import { Box, DialogActions, DialogContent, Grid, Stack } from "@mui/material";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Stepper from "@mui/material/Stepper";
import { MutationStatus, useQuery } from "@tanstack/react-query";
import { Form as FormikForm, FormikProvider, useFormik } from "formik";
import {
  Briefcase,
  Building2,
  DollarSign,
  GraduationCap,
  Save,
  User,
} from "lucide-react";
import React, { FC, useMemo, useState } from "react";
import * as yup from "yup";

// COMPONENTS:

import { getCustomers, getHubspotCustomer } from "api/customers";
// import FormikAutocomplete from "components/forms/FormikAutocomplete";
import { customersKeys } from "api/customers/queries";
// import { CustomersType } from "api/customers/types";
import FormikAutocomplete from "components/forms/FormikAutocomplete";
import FormikAutocompleteMultiple from "components/forms/FormikAutocompleteMultiple";
import FormikCheckbox from "components/forms/FormikCheckbox";
import FormikDatePicker from "components/forms/FormikDatePicker";
import FormikNumberField from "components/forms/FormikNumberField";
import FormikTextField from "components/forms/FormikTextField";
import MaskSocialInput from "components/forms/FormikTextField/MaskSocialInput";
import FormSection from "components/forms/FormSection";
import UltimateButton from "components/UltimateButton";
import { generateCustomerValues } from "utils/generateCustomerValues/generateCustomerValues";

import CustomerFormStep1, {
  CustomerFormStep1Values,
} from "./CustomerFormStep1";
import HubspotLeadSourceField from "./HubspotLeadSourceField";

export type CustomerFormValues = any;
interface ICustomerForm {
  handleSubmit: (values: CustomerFormValues) => void;
  handleClose: () => void;
  initialValues?: CustomerFormValues;
  submitStatus?: MutationStatus;
  edit?: boolean;
  customerId?: string;
}

const CustomerForm: FC<ICustomerForm> = ({
  handleSubmit,
  handleClose,
  edit,
  customerId,
  initialValues = {
    spouse: "",
    // GENERAL INFORMATION
    firstName: "",
    middleName: "",
    lastName: "",
    salutation: "",
    birthday: null,
    address: "",
    city: "",
    state: "",
    zip: "",
    phone: "",
    mobilePhone: "",
    referralSource: "",
    email: "",

    // Additional
    associatedBrand: "",
    leadSource: "",
    franchiseChoice: "",
    submissionEmail: "",
    submissionPassword: "",

    // PERSONAL INFORMATION
    personalInfo: {
      driversLicenseId: null,
      driversLicenseIssueDate: null,
      driversLicenseExpireDate: null,
      creditUnion: null,
      personalBank: null,
      militaryStatus: null,
      militaryAffiliation: null,
      bankRoutingNumber: null,
      bankAccountNumber: null,
      creditRepairBefore: false,
      judgementsLiensBankruptcy: null,
      previoiusFinanceCompany: false,
      maritalStatus: null,
    },
    // HOUSING INFO
    housingInfo: {
      houstingStatus: null,
      monthlyHousingPayment: null,
      estimatedLengthOfTimeAtResidence: null,
      moveInDate: null,
      calculatedLengthOfTimeAtResidence: null,
      yearsAtCurrentAddress: null,
    },
    // EMPLOYMENT INFO
    employmentInfo: {
      incomeType: null,
      employerName: null,
      employerPhone: null,
      employerAddress: null,
      estimatedTimeAtJob: null,
      startDateWithEmployer: null,
      calculatedLengthOfEmployment: null,
      occupationPosition: null,
      monthlyGrossIncome: null,
      annualPersonalIncome: null,
      frontEndRtiRatio: null,
      totalAnnualHouseholdIncome: null,
      householdFrontEndDtiRatio: null,
      statedMonthlyIncome: null,
      statedAnnualIncome: null,
      statedAnnualHouseholdIncome: null,
    },
    // SECURITY QUESTIONS
    securityQuestions: {
      birthCity: null,
      bronInForeignCountry: false,
      legalPermanentResident: false,
      greenCardExpirationDate: null,
      mothersMaidenName: null,
      highSchoolMascot: null,
      highSchoolCity: null,
      nameOfStreetYouGrewUp: null,
      favoritePetsName: null,
    },
    // EDUCATION
    educationInfo: {
      collegeAttended: null,
      fieldOfStudy: null,
      degree: null,
      graduatedDate: null,
      graduateSchoolAttended: null,
      graduateSchoolFieldOfStudy: null,
      graduateDegreeReceived: null,
      graduateGraduationDate: null,
    },
    // ASSET INFORMATION
    assetInfo: {
      combinedCheckingSavingsBalance: null,
      stocksBondsMutualFunds: null,
      retirementAccountBalance: null,
    },
    // PRIMARY RESIDENCE VALUATION
    primaryResidenceValuation: {
      avmValue: null,
      marketValue: null,
      zillowValue: null,
      estimatedPropertyValue: null,
      estimatedEquityPrimaryResidence: null,
      calculatedValue: null,
      calculatedEquity: null,
    },
  },
  submitStatus,
}) => {
  const schema = yup.object({
    firstName: yup.string().required(),
    lastName: yup.string().required(),
    address: yup.string().required(),
    city: yup.string().required(),
    state: yup.string().required(),
    zip: yup.string().required(),
    social: yup.string(),
    email: yup.string().email("Wrong email format").required(),
    birthday: yup.string().required(),
    associatedBrand: yup.string().required(),
  });

  const [hubspotUserSearching, setHubspotUserSearching] =
    useState<boolean>(false);
  const [activeStep, setActiveStep] = useState(edit ? 1 : 0);
  const [form2Values, setForm2Values] = useState(initialValues);

  const { data: spouses, isLoading: areSpousesLoading } = useQuery(
    customersKeys.list(),
    async () => {
      const { data: res } = await getCustomers({ limit: 1000 });
      return res.data;
    },
  );

  const spouseOptions = useMemo(() => {
    if (spouses?.length) {
      let filteredSpouses = spouses.filter(
        (spouse) => spouse._id !== customerId,
      );
      return filteredSpouses.map((spouse) => ({
        value: spouse._id,
        label: `${spouse.firstName} ${spouse.lastName}`,
      }));
    }

    return [];
  }, [spouses, customerId]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: form2Values,
    validationSchema: schema,
    onSubmit: handleSubmit,
  });

  const steps = ["Enter email", "Enter other info"];

  const handleStep1Submit = async (values: CustomerFormStep1Values) => {
    try {
      setHubspotUserSearching(true);
      const { data } = await getHubspotCustomer(values?.email);
      setHubspotUserSearching(false);
      setForm2Values({
        ...generateCustomerValues(data?.data),
        email: values?.email,
      });
      setActiveStep(1);
    } catch (err) {
      setHubspotUserSearching(false);
    }
  };

  const CustomerFormStepper = () => {
    return (
      <Box maxWidth={600} mb={4} mx="auto">
        <Stepper activeStep={activeStep}>
          {steps.map((label, index) => {
            const stepProps: { completed?: boolean } = {};
            const labelProps: {
              optional?: React.ReactNode;
            } = {};
            return (
              <Step key={label} {...stepProps}>
                <StepLabel {...labelProps}>{label}</StepLabel>
              </Step>
            );
          })}
        </Stepper>
      </Box>
    );
  };

  return (
    <>
      {activeStep === 0 && (
        <CustomerFormStep1
          handleClose={handleClose}
          submitStatus={submitStatus}
          handleSubmit={handleStep1Submit}
          customerFormStepper={<CustomerFormStepper />}
          hubspotUserSearching={hubspotUserSearching}
        />
      )}

      {activeStep === 1 && (
        <FormikProvider value={formik}>
          <FormikForm>
            <DialogContent>
              {!edit && <CustomerFormStepper />}
              <Stack spacing={3} sx={{ mt: 2 }}>
                <FormSection
                  title="General Information"
                  description="Customer contact details, address, and basic information."
                  Icon={User}
                >
                  <Grid container spacing={2} rowSpacing={1}>
                    {/* 1 */}
                    <Grid item xs={12} lg={3}>
                      <FormikTextField label="Salutation" name={"salutation"} />
                    </Grid>
                    <Grid item xs={12} lg={3}>
                      <FormikTextField label="First Name" name={"firstName"} />
                    </Grid>
                    <Grid item xs={12} lg={3}>
                      <FormikTextField
                        label="Middle Name"
                        name={"middleName"}
                      />
                    </Grid>
                    <Grid item xs={12} lg={3}>
                      <FormikTextField label="Last Name" name={"lastName"} />
                    </Grid>

                    <Grid item xs={12} lg={3}>
                      <FormikTextField label="Address" name={"address"} />
                    </Grid>
                    <Grid item xs={12} lg={3}>
                      <FormikTextField label="City" name={"city"} />
                    </Grid>
                    <Grid item xs={12} lg={3}>
                      <FormikTextField label="State" name={"state"} />
                    </Grid>
                    <Grid item xs={12} lg={3}>
                      <FormikTextField label="ZIP" name={"zip"} />
                    </Grid>
                    <Grid item xs={12} lg={6}>
                      <FormikTextField label="Phone" name="phone" />
                    </Grid>
                    <Grid item xs={12} lg={6}>
                      <FormikTextField
                        label="Mobilephone"
                        name={"mobilePhone"}
                      />
                    </Grid>

                    <Grid item xs={12} lg={12}>
                      <FormikTextField
                        label="Social"
                        name={"social"}
                        InputProps={{
                          inputComponent: MaskSocialInput as any,
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} lg={6}>
                      <FormikAutocomplete
                        label="Marital Status"
                        name={"personalInfo.maritalStatus"}
                        options={[
                          { value: "Single", label: "Single" },
                          { value: "Married", label: "Married" },
                          { value: "Divorced", label: "Divorced" },
                          {
                            value: "Prefer not to answer",
                            label: "Prefer not to answer",
                          },
                        ]}
                      />
                    </Grid>
                    <Grid item xs={12} lg={6}>
                      <FormikAutocomplete
                        label="Spouse"
                        name="spouse"
                        options={spouseOptions}
                        loading={areSpousesLoading}
                      />
                    </Grid>
                    <Grid item xs={12} lg={12}>
                      <FormikTextField label="Email" name={"email"} />
                    </Grid>
                    <Grid item xs={12} lg={12}>
                      <FormikTextField
                        label="Place of Birth"
                        name={"securityQuestions.birthCity"}
                      />
                    </Grid>
                    <Grid item xs={12} lg={12}>
                      <FormikCheckbox
                        label="Born in foreign country"
                        name={"securityQuestions.bronInForeignCountry"}
                      />
                    </Grid>
                    <Grid item xs={12} lg={12}>
                      <FormikCheckbox
                        label="Legal Permanent Resident"
                        name={"securityQuestions.legalPermanentResident"}
                      />
                    </Grid>
                    <Grid item xs={12} lg={12}>
                      <FormikDatePicker
                        label="Green Card Expiration Date"
                        name={"securityQuestions.greenCardExpirationDate"}
                      />
                    </Grid>
                    <Grid item xs={12} lg={12}>
                      <FormikDatePicker
                        label="Birthday"
                        name={"birthday"}
                        timezone="UTC"
                      />
                    </Grid>
                    <Grid item xs={12} lg={12}>
                      <FormikTextField
                        label="Mothers Maiden Name"
                        name={"securityQuestions.mothersMaidenName"}
                      />
                    </Grid>
                    <Grid item xs={12} lg={12}>
                      <FormikTextField
                        label="High School Mascot"
                        name={"securityQuestions.highSchoolMascot"}
                      />
                    </Grid>
                    <Grid item xs={12} lg={12}>
                      <FormikTextField
                        label="High School City"
                        name={"securityQuestions.highSchoolCity"}
                      />
                    </Grid>
                    <Grid item xs={12} lg={12}>
                      <FormikTextField
                        label="Name of Street you grew up on"
                        name={"securityQuestions.nameOfStreetYouGrewUp"}
                      />
                    </Grid>
                    <Grid item xs={12} lg={12}>
                      <FormikTextField
                        label="Name of Favorite Pet"
                        name={"securityQuestions.favoritePetsName"}
                      />
                    </Grid>

                    {/* UNRECOGNISED */}
                    <Grid item xs={12} lg={3}>
                      <FormikTextField
                        label="Franchise Choice"
                        name="franchiseChoice"
                      />
                    </Grid>
                    <Grid item xs={12} lg={3}>
                      <FormikTextField
                        label="Associate Brand"
                        name={"associatedBrand"}
                      />
                    </Grid>
                    <Grid item xs={12} lg={3}>
                      <FormikTextField
                        label="Referral Source"
                        name={"referralSource"}
                      />
                    </Grid>
                    <Grid item xs={12} lg={3}>
                      <HubspotLeadSourceField />
                    </Grid>
                    <Grid item xs={12} lg={6}>
                      <FormikTextField
                        label="Submission Email"
                        name="submissionEmail"
                      />
                    </Grid>
                    <Grid item xs={12} lg={6}>
                      <FormikTextField
                        label="Submission Password"
                        name="submissionPassword"
                      />
                    </Grid>

                    {/* UNRECOGNISED */}
                  </Grid>
                </FormSection>

                <FormSection
                  title="Personal & Education"
                  description="Driver's license, housing, education, and military information."
                  Icon={GraduationCap}
                >
                  <Grid container spacing={2} rowSpacing={1}>
                    <Grid item xs={12} lg={12}>
                      <FormikTextField
                        label="Driver's License Number"
                        name={"personalInfo.driversLicenseId"}
                      />
                    </Grid>
                    <Grid item xs={12} lg={12}>
                      <FormikDatePicker
                        label="Driver's License Issuance Date"
                        name={"personalInfo.driversLicenseIssueDate"}
                      />
                    </Grid>
                    <Grid item xs={12} lg={12}>
                      <FormikDatePicker
                        label="Driver's License Expiration Date"
                        name={"personalInfo.driversLicenseExpireDate"}
                      />
                    </Grid>
                    <Grid item xs={12} lg={12}>
                      <FormikAutocomplete
                        label="Housing Status"
                        name={"housingInfo.houstingStatus"}
                        options={[
                          {
                            value: "Own",
                            label: "Own",
                          },
                          {
                            value: "Rent",
                            label: "Rent",
                          },
                          {
                            value: "Live with Family",
                            label: "Live with Family",
                          },
                          {
                            value: "Other",
                            label: "Other",
                          },
                        ]}
                      />
                    </Grid>
                    <Grid item xs={12} lg={12}>
                      <FormikNumberField
                        label="Monthly Housing Payment"
                        name={"housingInfo.monthlyHousingPayment"}
                        prefix="$"
                      />
                    </Grid>

                    <Grid item xs={12} lg={3}>
                      <FormikDatePicker
                        label="Move in Date"
                        name={"housingInfo.moveInDate"}
                      />
                    </Grid>
                    <Grid item xs={12} lg={3}>
                      <FormikTextField
                        label="Time at Residence"
                        name={"housingInfo.calculatedLengthOfTimeAtResidence"}
                        disabled
                      />
                    </Grid>
                    <Grid item xs={12} lg={3}>
                      <FormikNumberField
                        label="Years at Current Addresss"
                        name={"housingInfo.yearsAtCurrentAddress"}
                      />
                    </Grid>
                    <Grid item xs={12} lg={3}>
                      <FormikNumberField
                        label="Estimated Length of time at Residence"
                        name={"housingInfo.estimatedLengthOfTimeAtResidence"}
                      />
                    </Grid>
                    <Grid item xs={12} lg={12}>
                      <FormikNumberField
                        label="Monthly Personal Gross Income"
                        name={"employmentInfo.monthlyGrossIncome"}
                        prefix="$"
                      />
                    </Grid>
                    <Grid item xs={12} lg={12}>
                      <FormikNumberField
                        label="Annual Personal Income"
                        name={"employmentInfo.annualPersonalIncome"}
                        prefix="$"
                      />
                    </Grid>
                    <Grid item xs={12} lg={12}>
                      <FormikNumberField
                        label="Annual Household Income"
                        name={"employmentInfo.totalAnnualHouseholdIncome"}
                        prefix="$"
                      />
                    </Grid>
                    <Grid item xs={12} lg={12}>
                      <FormikTextField
                        label="Member of Credit Union"
                        name={"personalInfo.creditUnion"}
                      />
                    </Grid>
                    <Grid item xs={12} lg={12}>
                      <FormikTextField
                        label="Personal Banking Relationship"
                        name={"personalInfo.personalBank"}
                      />
                    </Grid>
                    <Grid item xs={12} lg={12}>
                      <FormikTextField
                        label="Bank Routing Number"
                        name={"personalInfo.bankRoutingNumber"}
                      />
                    </Grid>
                    <Grid item xs={12} lg={12}>
                      <FormikTextField
                        label="Bank Account Number"
                        name={"personalInfo.bankAccountNumber"}
                      />
                    </Grid>

                    <Grid item xs={12} lg={12}>
                      <FormikCheckbox
                        label="Has Credit Repair Before"
                        name={"personalInfo.creditRepairBefore"}
                      />
                    </Grid>
                    <Grid item xs={12} lg={12}>
                      <FormikTextField
                        label="BK in past 20 years"
                        name={"personalInfo.judgementsLiensBankruptcy"}
                      />
                    </Grid>
                    <Grid item xs={12} lg={12}>
                      <FormikCheckbox
                        label="Worked with a Finance Company"
                        name={"personalInfo.previoiusFinanceCompany"}
                      />
                    </Grid>

                    <Grid item xs={12} lg={3}>
                      <FormikTextField
                        label="College/University Attended"
                        name={"educationInfo.collegeAttended"}
                      />
                    </Grid>
                    <Grid item xs={12} lg={3}>
                      <FormikTextField
                        label="Field of study"
                        name={"educationInfo.fieldOfStudy"}
                      />
                    </Grid>
                    <Grid item xs={12} lg={3}>
                      <FormikTextField
                        label="Degree"
                        name={"educationInfo.degree"}
                      />
                    </Grid>
                    <Grid item xs={12} lg={3}>
                      <FormikDatePicker
                        label="Graduation date"
                        name={"educationInfo.graduatedDate"}
                      />
                    </Grid>

                    <Grid item xs={12} lg={3}>
                      <FormikTextField
                        label="Graduate School Attended"
                        name={"educationInfo.graduateSchoolAttended"}
                      />
                    </Grid>
                    <Grid item xs={12} lg={3}>
                      <FormikTextField
                        label="Graduate School Field of Study"
                        name={"educationInfo.graduateSchoolFieldOfStudy"}
                      />
                    </Grid>
                    <Grid item xs={12} lg={3}>
                      <FormikTextField
                        label="Graduate Degree Received"
                        name={"educationInfo.graduateDegreeReceived"}
                      />
                    </Grid>
                    <Grid item xs={12} lg={3}>
                      <FormikTextField
                        label="Graduate Graduation date"
                        name={"educationInfo.graduateGraduationDate"}
                      />
                    </Grid>
                    <Grid item xs={12} lg={12}>
                      <FormikAutocomplete
                        label="Military Status"
                        name={"personalInfo.militaryStatus"}
                        options={[
                          {
                            value: "Active Duty",
                            label: "Active Duty",
                          },
                          {
                            value: "Retired",
                            label: "Retired",
                          },
                          {
                            value: "Family Member",
                            label: "Family Member",
                          },
                          {
                            value: "Reserves",
                            label: "Reserves",
                          },
                          {
                            value: "Veteran",
                            label: "Veteran",
                          },
                          {
                            value: "Non-Military",
                            label: "Non-Military",
                          },
                          {
                            value: "Military Spouse",
                            label: "Military Spouse",
                          },
                        ]}
                      />
                    </Grid>
                    <Grid item xs={12} lg={12}>
                      <FormikTextField
                        label="Military Affiliation"
                        name={"personalInfo.militaryAffiliation"}
                      />
                    </Grid>
                  </Grid>
                </FormSection>

                <FormSection
                  title="Employment Info"
                  description="Employer details, income, and job history."
                  Icon={Briefcase}
                >
                  <Grid container spacing={2} rowSpacing={1}>
                    <Grid item xs={12} lg={12}>
                      <FormikTextField
                        label="Employer"
                        name={"employmentInfo.employerName"}
                      />
                    </Grid>
                    <Grid item xs={12} lg={12}>
                      <FormikTextField
                        label="Employer Phone Number"
                        name={"employmentInfo.employerPhone"}
                      />
                    </Grid>
                    <Grid item xs={12} lg={12}>
                      <FormikTextField
                        label="Employer Address"
                        name={"employmentInfo.employerAddress"}
                      />
                    </Grid>
                    <Grid item xs={12} lg={4}>
                      <FormikTextField
                        label="Est. time at job"
                        name={"employmentInfo.estimatedTimeAtJob"}
                      />
                    </Grid>
                    <Grid item xs={12} lg={4}>
                      <FormikDatePicker
                        label="Start Date with Employer"
                        name={"employmentInfo.startDateWithEmployer"}
                      />
                    </Grid>
                    <Grid item xs={12} lg={4}>
                      <FormikTextField
                        label="Calculated Length of Employment"
                        name={"employmentInfo.calculatedLengthOfEmployment"}
                        disabled
                      />
                    </Grid>
                    <Grid item xs={12} lg={12}>
                      <FormikTextField
                        label="Occupation/Position"
                        name={"employmentInfo.occupationPosition"}
                      />
                    </Grid>
                    {/* 1 */}
                    <Grid item xs={12} lg={12}>
                      <FormikAutocompleteMultiple
                        label="Income Type"
                        name={"employmentInfo.incomeType"}
                        options={[
                          {
                            value: "W2",
                            label: "W2",
                          },
                          {
                            value: "Business Owner",
                            label: "Business Owner",
                          },
                          {
                            value: "1099",
                            label: "1099",
                          },
                          {
                            value: "Retired",
                            label: "Retired",
                          },
                          {
                            value: "Other",
                            label: "Other",
                          },
                        ]}
                      />
                    </Grid>

                    <Grid item xs={12} lg={12}>
                      <FormikNumberField
                        label="Stated Monthly Income"
                        name={"employmentInfo.statedMonthlyIncome"}
                        prefix="$"
                      />
                    </Grid>
                    <Grid item xs={12} lg={12}>
                      <FormikNumberField
                        label="Stated Annual Income"
                        name={"employmentInfo.statedAnnualIncome"}
                        prefix="$"
                      />
                    </Grid>
                    <Grid item xs={12} lg={12}>
                      <FormikNumberField
                        label="Stated Annual Household Income"
                        name={"employmentInfo.statedAnnualHouseholdIncome"}
                        prefix="$"
                      />
                    </Grid>
                  </Grid>
                </FormSection>

                <FormSection
                  title="Asset Info"
                  description="Checking, savings, investments, and primary residence valuation."
                  Icon={DollarSign}
                >
                  <Grid container spacing={2} rowSpacing={1}>
                    <Grid item xs={12} lg={12}>
                      <FormikNumberField
                        label="Combined Checking & Savings Balance"
                        name={"assetInfo.combinedCheckingSavingsBalance"}
                        prefix="$"
                      />
                    </Grid>
                    <Grid item xs={12} lg={12}>
                      <FormikNumberField
                        label="Stocks, Bonds & Mutual Funds"
                        name={"assetInfo.stocksBondsMutualFunds"}
                        prefix="$"
                      />
                    </Grid>
                    <Grid item xs={12} lg={12}>
                      <FormikNumberField
                        label="Retirement Account Balance"
                        name={"assetInfo.retirementAccountBalance"}
                        prefix="$"
                      />
                    </Grid>
                    <Grid item xs={12} lg={12}>
                      <FormikNumberField
                        label="AVM in Response"
                        name={"primaryResidenceValuation.avmValue"}
                      />
                    </Grid>
                    <Grid item xs={12} lg={12}>
                      <FormikNumberField
                        label="Market Value in Response"
                        name={"primaryResidenceValuation.marketValue"}
                      />
                    </Grid>
                    <Grid item xs={12} lg={12}>
                      <FormikNumberField
                        label="Zillow Value"
                        name={"primaryResidenceValuation.zillowValue"}
                      />
                    </Grid>
                    <Grid item xs={12} lg={3}>
                      <FormikNumberField
                        label="Est. Value of Primary Residence"
                        name={
                          "primaryResidenceValuation.estimatedPropertyValue"
                        }
                        prefix="$"
                      />
                    </Grid>
                    <Grid item xs={12} lg={3}>
                      <FormikNumberField
                        label="Est. Equity in Primary Residence"
                        name={
                          "primaryResidenceValuation.estimatedEquityPrimaryResidence"
                        }
                        prefix="$"
                      />
                    </Grid>
                    <Grid item xs={12} lg={3}>
                      <FormikNumberField
                        label="Cal. Value of Primary Residence"
                        name={"primaryResidenceValuation.calculatedValue"}
                        prefix="$"
                      />
                    </Grid>
                    <Grid item xs={12} lg={3}>
                      <FormikNumberField
                        label="Cal. Equity of Primary Residence"
                        name={"primaryResidenceValuation.calculatedEquity"}
                        prefix="$"
                      />
                    </Grid>
                  </Grid>
                </FormSection>

                <FormSection
                  title="Business Info"
                  description="Business banking and account details."
                  Icon={Building2}
                >
                  <Grid container spacing={2} rowSpacing={1}>
                    <Grid item xs={12} lg={12}>
                      <FormikTextField
                        label="Business Account Number"
                        name={"personalInfo.businessBankAccountNumber"}
                      />
                    </Grid>
                    <Grid item xs={12} lg={12}>
                      <FormikTextField
                        label="Business Account Routing Number"
                        name={"personalInfo.businessBankRoutingNumber"}
                      />
                    </Grid>
                    <Grid item xs={12} lg={12}>
                      <FormikTextField
                        label="Business Banking Relationship"
                        name={"personalInfo.businessBank"}
                      />
                    </Grid>
                  </Grid>
                </FormSection>
              </Stack>
            </DialogContent>

            <DialogActions>
              <UltimateButton
                type="submit"
                text="Save"
                Icon={
                  Save as React.ComponentType<{
                    size?: number;
                    className?: string;
                    style?: React.CSSProperties;
                  }>
                }
                loading={submitStatus === "loading"}
                sx={{
                  minWidth: 150,
                  py: 1.5,
                }}
              />
            </DialogActions>
          </FormikForm>
        </FormikProvider>
      )}
    </>
  );
};

export default CustomerForm;
